import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Button, DatePicker, Form, Select, Input, Pagination, Switch, Tooltip, Modal, message } from 'antd';
import { FilterOutlined, CloseOutlined, SearchOutlined, InfoCircleOutlined, NotificationOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import api from '../../services/api';
import { SL_Grid } from '../../components/grid/SL_Grid';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Rótulos amigáveis para os tipos de evento de pontos.
const EVENT_TYPE_LABELS = {
    exercise_completed: 'Exercício concluído',
    workout_completed: 'Treino finalizado',
    diet_opened: 'Dieta visualizada',
    video_watched: 'Vídeo assistido',
};

const PAGE_SIZE = 40;

// Janela padrão: últimos 7 dias (incluindo hoje).
const DEFAULT_FROM = () => dayjs().subtract(6, 'day');
const DEFAULT_TO = () => dayjs();

// Colunas do grid de auditoria (lista de eventos).
const auditColumns = [
    {
        field: 'occurred_at',
        headerName: 'Data/Hora',
        flex: 1,
        minWidth: 150,
        valueFormatter: ({ value }) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
        field: 'user',
        headerName: 'Usuário',
        flex: 1.4,
        minWidth: 180,
        valueGetter: ({ value }) => {
            if (!value) return '-';
            const name = `${value.first_name ?? ''} ${value.last_name ?? ''}`.trim();
            return name || value.email || `#${value.id}`;
        },
    },
    {
        field: 'email',
        headerName: 'E-mail',
        flex: 1.4,
        minWidth: 180,
        valueGetter: ({ row }) => row?.user?.email || '-',
    },
    {
        field: 'event_type',
        headerName: 'Evento',
        flex: 1,
        minWidth: 160,
        valueFormatter: ({ value }) => EVENT_TYPE_LABELS[value] || value,
    },
    { field: 'points', headerName: 'Pontos', width: 90, type: 'number' },
    {
        field: 'ref',
        headerName: 'Referência',
        flex: 1,
        minWidth: 140,
        valueGetter: ({ row }) => (row?.ref_type ? `${row.ref_type}${row.ref_id != null ? ` #${row.ref_id}` : ''}` : '-'),
    },
];

// Colunas do modo "usuários sem esses eventos".
const withoutColumns = [
    {
        field: 'user_name',
        headerName: 'Usuário',
        flex: 1.6,
        minWidth: 200,
        valueGetter: ({ row }) => {
            const name = `${row?.first_name ?? ''} ${row?.last_name ?? ''}`.trim();
            return name || row?.email || `#${row?.user_id}`;
        },
    },
    {
        field: 'email',
        headerName: 'E-mail',
        flex: 1.6,
        minWidth: 220,
        valueGetter: ({ row }) => row?.email || '-',
    },
    {
        field: 'events_in_period',
        headerName: 'Eventos no período',
        width: 150,
        type: 'number',
        valueGetter: ({ row }) => Number(row?.events_in_period || 0),
    },
    {
        field: 'points_in_period',
        headerName: 'Pontos no período',
        width: 150,
        type: 'number',
        valueGetter: ({ row }) => Number(row?.points_in_period || 0),
    },
];

export default function ListPointsAudit() {
    const { mobile_mode } = useContext(GeneralContext);
    const [form] = Form.useForm();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [isFiltersVisible, setIsFiltersVisible] = useState(true);
    const [withoutMode, setWithoutMode] = useState(false);

    const buildParams = useCallback((values, nextPage) => {
        const params = { page: nextPage, page_size: PAGE_SIZE };
        if (values) {
            if (values.user_search && String(values.user_search).trim()) {
                params.user_search = String(values.user_search).trim();
            }
            // Um ou mais tipos de evento (csv).
            if (Array.isArray(values.event_types) && values.event_types.length) {
                params.event_types = values.event_types.join(',');
            }
            if (values.date_range && values.date_range[0] && values.date_range[1]) {
                params.date_from = dayjs(values.date_range[0]).format('YYYY-MM-DD');
                params.date_to = dayjs(values.date_range[1]).format('YYYY-MM-DD');
            }
        }
        return params;
    }, []);

    const loadAudit = useCallback(
        async (values, nextPage, mode) => {
            const params = buildParams(values, nextPage);
            const useWithout = !!mode;
            setLoading(true);
            try {
                const { data } = useWithout
                    ? await api.points.auditWithout(params)
                    : await api.points.audit(params);
                setRows(data.rows || []);
                setTotal(data.total || 0);
                setPage(data.page || 1);
            } catch (e) {
                // erro já tratado pelo interceptor
            } finally {
                setLoading(false);
            }
        },
        [buildParams]
    );

    useEffect(() => {
        // Default: últimos 7 dias já filtrados.
        const initial = [DEFAULT_FROM(), DEFAULT_TO()];
        form.setFieldsValue({ date_range: initial });
        loadAudit({ date_range: initial }, 1, false);
    }, [loadAudit, form]);

    const onSubmitFilters = (values) => loadAudit(values, 1, withoutMode);
    const onClearFilters = () => {
        const initial = [DEFAULT_FROM(), DEFAULT_TO()];
        form.setFieldsValue({ date_range: initial, user_search: undefined, event_types: [] });
        loadAudit({ date_range: initial }, 1, withoutMode);
    };

    const onPageChange = (nextPage) => {
        const values = form.getFieldsValue();
        loadAudit(values, nextPage, withoutMode);
    };

    const onToggleWithoutMode = (checked) => {
        setWithoutMode(checked);
        loadAudit(form.getFieldsValue(), 1, checked);
    };

    // ===== Notificação em lote para os usuários filtrados =====
    const [notifyModalOpen, setNotifyModalOpen] = useState(false);
    const [notifyCount, setNotifyCount] = useState(0);
    const [notifyPreviewUsers, setNotifyPreviewUsers] = useState([]);
    const [notifyLoading, setNotifyLoading] = useState(false);
    const [notifySending, setNotifySending] = useState(false);
    const [notifyForm] = Form.useForm();

    // Constrói o objeto de filtro a partir do formulário + modo atual (compartilhado
    // entre preview e envio, garantindo que o back resolva exatamente o que está no grid).
    const buildNotifyFilter = useCallback(() => {
        const values = form.getFieldsValue();
        const filter = {
            mode: withoutMode ? 'without' : 'audit',
            user_search: values.user_search ? String(values.user_search).trim() : undefined,
            event_types: Array.isArray(values.event_types) ? values.event_types : [],
            date_from: values.date_range && values.date_range[0] ? dayjs(values.date_range[0]).format('YYYY-MM-DD') : undefined,
            date_to: values.date_range && values.date_range[1] ? dayjs(values.date_range[1]).format('YYYY-MM-DD') : undefined,
        };
        return filter;
    }, [form, withoutMode]);

    const openNotifyModal = async () => {
        setNotifyModalOpen(true);
        setNotifyLoading(true);
        setNotifyCount(0);
        setNotifyPreviewUsers([]);
        try {
            const filter = buildNotifyFilter();
            const params = { ...filter, event_types: filter.event_types.join(',') };
            const { data } = await api.points.notifyPreview(params);
            setNotifyCount(data.count || 0);
            setNotifyPreviewUsers(data.users || []);
        } catch (e) {
            // erro tratado pelo interceptor
        } finally {
            setNotifyLoading(false);
        }
    };

    const sendNotify = async () => {
        try {
            const values = await notifyForm.validateFields();
            setNotifySending(true);
            const filter = buildNotifyFilter();
            const { data } = await api.points.notify({
                ...filter,
                title: values.title,
                description: values.message,
            });
            message.success(`Notificação enviada para ${data.sent_to} usuário(s).`);
            setNotifyModalOpen(false);
            notifyForm.resetFields();
        } catch (e) {
            // validateFields lança em campos inválidos; erros de rede vão pro interceptor
        } finally {
            setNotifySending(false);
        }
    };

    return (
        <div className="flex flex-col flex-1 h-full">
            <div className={`flex w-full bg-fit-red-700 p-2 rounded-tl-[4px] rounded-tr-[4px] flex-wrap ${mobile_mode ? 'flex-col items-center gap-4' : 'gap-2'}`}>
                <div className="self-center text-white font-semibold text-base">
                    Auditoria de Pontos
                    <span className="text-white/80 font-normal text-sm ml-2">(últimos 7 dias)</span>
                </div>
                <div className="flex-1 flex flex-wrap gap-2 justify-end">
                    <Button
                        type="primary"
                        icon={<NotificationOutlined />}
                        onClick={openNotifyModal}
                        style={{ fontWeight: 'bold', borderRadius: 7 }}
                    >
                        Enviar notificação
                    </Button>
                    <Button
                        type="default"
                        shape="circle"
                        onClick={() => setIsFiltersVisible((v) => !v)}
                        icon={!isFiltersVisible ? <FilterOutlined className="text-fit-red-800 flex items-center justify-center" /> : <CloseOutlined className="text-fit-red-800 flex items-center justify-center" />}
                    />
                </div>
            </div>

            {isFiltersVisible && (
                <div className="w-full border border-gray-300 bg-fit-gray-100 p-3">
                    <Form
                        form={form}
                        layout="inline"
                        onFinish={onSubmitFilters}
                        className="flex flex-wrap gap-4"
                    >
                        <Form.Item name="user_search" label="Usuário" className="m-0">
                            <Input
                                allowClear
                                placeholder="Nome ou e-mail"
                                style={{ width: 240 }}
                                prefix={<SearchOutlined className="text-gray-400" />}
                            />
                        </Form.Item>

                        <Form.Item name="event_types" label="Eventos" className="m-0">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Todos os eventos"
                                style={{ minWidth: 260, maxWidth: 420 }}
                                maxTagCount="responsive"
                            >
                                {Object.entries(EVENT_TYPE_LABELS).map(([code, label]) => (
                                    <Option key={code} value={code}>{label}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="date_range" label="Período" className="m-0">
                            <RangePicker format="DD/MM/YYYY" />
                        </Form.Item>

                        <Form.Item valuePropName="checked" className="m-0">
                            <div className="flex items-center gap-2">
                                <Switch checked={withoutMode} onChange={onToggleWithoutMode} />
                                <span className="font-semibold text-fit-red-900 flex items-center gap-1">
                                    Listar usuários sem esses eventos
                                    <Tooltip title="Mostra os usuários que NÃO tiveram nenhum dos eventos filtrados no período (ex.: quem não concluiu treino na semana).">
                                        <InfoCircleOutlined className="text-gray-400" />
                                    </Tooltip>
                                </span>
                            </div>
                        </Form.Item>

                        <Form.Item className="m-0">
                            <Button type="primary" htmlType="submit" style={{ fontWeight: 'bold', borderRadius: 7 }}>
                                Buscar
                            </Button>
                        </Form.Item>
                        <Form.Item className="m-0">
                            <Button danger style={{ fontWeight: 'bold', borderRadius: 7 }} onClick={onClearFilters}>
                                Limpar
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            )}

            <div className="w-full flex-1 min-h-[400px] smb:min-h-0">
                <SL_Grid
                    rows={rows}
                    columns={withoutMode ? withoutColumns : auditColumns}
                    loading={loading}
                    pagination={false}
                    getRowId={(row) => (withoutMode ? row.user_id : row.id)}
                />
            </div>

            <div className="flex justify-end p-2 bg-white border-t border-gray-200">
                <Pagination
                    current={page}
                    total={total}
                    pageSize={PAGE_SIZE}
                    showSizeChanger={false}
                    onChange={onPageChange}
                    showTotal={(t) => `${t} ${withoutMode ? 'usuários' : 'eventos'}`}
                />
            </div>

            <Modal
                title="Enviar notificação para os usuários filtrados"
                open={notifyModalOpen}
                onCancel={() => setNotifyModalOpen(false)}
                okText="Enviar notificação"
                cancelText="Cancelar"
                confirmLoading={notifySending}
                okButtonProps={{ disabled: notifyLoading || notifyCount === 0 }}
                onOk={sendNotify}
                width={560}
            >
                <p className="text-gray-700 mb-3">
                    Será enviada <b>uma notificação</b> para todos os usuários que estão filtrados no grid
                    neste momento ({withoutMode ? <>usuários <b>sem</b> esses eventos</> : <>usuários <b>com</b> esses eventos</>}).
                    Usuários repetidos recebem somente <b>uma vez</b>. O registro fica no histórico de Notificações.
                </p>

                <div className="mb-3 p-2 bg-gray-100 rounded">
                    {notifyLoading ? (
                        <span>Calculando destinatários...</span>
                    ) : (
                        <>
                            <span className="font-bold text-fit-red-900">{notifyCount}</span>
                            <span className="ml-1">usuário(s) serão notificados</span>
                            {notifyPreviewUsers.length > 0 && (
                                <div className="mt-2 text-xs text-gray-500">
                                    {notifyPreviewUsers.map((u) => {
                                        const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || `#${u.id}`;
                                        return <div key={u.id}>• {name}</div>;
                                    })}
                                    {notifyCount > notifyPreviewUsers.length && <div>... e mais {notifyCount - notifyPreviewUsers.length}</div>}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <Form form={notifyForm} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Título"
                        rules={[{ required: true, message: 'Informe o título.' }]}
                    >
                        <Input placeholder="Título da notificação" maxLength={120} />
                    </Form.Item>
                    <Form.Item
                        name="message"
                        label="Mensagem"
                        rules={[{ required: true, message: 'Informe a mensagem.' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Mensagem enviada no push e no app" maxLength={500} showCount />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
